import type { PortalWebsiteReport } from '../../../../../types/portalWebsite';
import { WebsitePage } from './WebsitePage';
import { ClubwiseDataPage } from './ClubwiseDataPage';
import { useState, type ReactNode } from 'react';
import type {
  MetaGoalBucket,
  PortalComparison,
  PortalGoogleReport,
  PortalMetaReport,
  PortalOverviewReport,
  PortalReportConfig,
} from '../../../../../types/portalReport';
import {
  REPORT_MODE_ATTR,
  modeToggleLabel,
  otherMode,
  readReportMode,
  writeReportMode,
  type PortalReportMode,
} from '../../../../../lib/portalReport/reportTheme';
import {
  adGroupExclusionNote,
  periodNote,
} from '../../../../../lib/portalReport/reportCopy';
// ⚠️⚠️ `portalThemeStyle` IS DELIBERATELY NOT IMPORTED HERE. Scott, having seen
// the brand-font version live: "It looks weird using the brand's one. I like
// Kyck's better. But we will keep the logo of the brand." So the restyled
// report emits NO brand custom properties at all — no accent, no ink, no
// gradient, no fonts — and reads `--rpt-*` throughout. `logoUrl` / `logoAlt`
// are the ONLY brand-theme fields it consumes, and a test asserts it.
//
// ⚠️ The helper and every field it emits STAY. The EXISTING client report is
// still live for any account whose restyle flag is false, and it still reads
// them — removing either would restyle that report by accident.
import { Band, SectionHeading, SwitchGroup } from './controls';
import { ReportChartCard } from './ReportChartCard';
import {
  ReportHero,
  channelLeadsDelta,
  heroStatsFor,
  perCurrency,
  sumLeads,
} from './ReportHero';
import { groupAverages, hasLeaderboard } from '../../../../../lib/portalReport/locations';
import { PortalLockup } from '../../PortalLockup';
import {
  AdGroupsTable,
  ChannelCampaignsTable,
  CombinedCampaignsTable,
  GroupAverageTiles,
  LocationsTable,
  SearchTermsTable,
} from './ReportTables';
import { isGroupScope } from '../../../../../lib/portalReport/groupScope';
import {
  AudiencePanel,
  ChannelCard,
  CompetitivePanel,
  CreativeGrid,
  DeliveryDiagnostics,
  ProvenanceBlock,
} from './ReportPanels';
import { CrmCharts, LeadSources } from './CrmPanel';
import { CrmHeroBand } from './CrmHeroBand';
import { GroupChartsBand } from './GroupChartsBand';
import { MetaGoalFilterButtons, MetaGoalHeadline, MetaGoalPanels } from './MetaGoalPanels';
import { AutomotiveHome } from './AutomotiveHome';
import { AutomotiveGooglePage } from './AutomotiveGooglePanels';
import { AutomotiveMetaPage } from './AutomotiveMetaPanels';
import { goalSplitLabels } from '../../../../../lib/portalReport/config';
import { TRACKING_NOTICE } from '../../../../../lib/portalReport/metaGoalReport';
import { DASH, formatReportDate, moneyFull, moneyRound, numberFull } from './format';

// CLIENT PORTAL RESTYLE — the report itself.
//
// ⚠️⚠️ A PARALLEL IMPLEMENTATION, SELECTED BY A FLAG. The existing
// ReportPages / ReportLayout / ChannelBlock path is UNTOUCHED and keeps serving
// portal users, so a rollback is a flag flip rather than a revert. Nothing in
// this folder is imported by the live client path.
//
// ⚠️ It renders INSIDE an element carrying `.kyck-portal` — the token scope and
// the brand accent variables both hang off that. The mode attribute goes on the
// SAME element, never on `:root` and never on `html`.

export type ReportTab = 'overview' | 'meta' | 'google' | 'website' | 'clubwise';

/**
 * The tab strip, in order.
 *
 * ⚠️ A module constant so the nav can FILTER it rather than branch inside a
 * `.map()`. When `showsGoogle` is false the Google entry is removed from the
 * list, so there is no tab and no gap where one was.
 */
const TAB_LABELS: ReadonlyArray<readonly [ReportTab, string]> = [
  ['overview', 'Overview'],
  ['meta', 'Meta'],
  ['google', 'Google'],
  // ⚠️ Present only when the brand has a GA4 property AND the scope resolves to
  // an audience on it (`showsWebsite`, server-side) — the `showsGoogle` device.
  ['website', 'Website Analytics'],
  // Present only for a ClubWise brand (`showsClubwise`, server-side).
  ['clubwise', 'ClubWise data'],
];

export interface RestyledReportProps {
  tab: ReportTab;
  onTabChange: (tab: ReportTab) => void;
  overview: PortalOverviewReport | null;
  meta: PortalMetaReport | null;
  google: PortalGoogleReport | null;
  website: PortalWebsiteReport | null;
  config: PortalReportConfig;
  /** The client's own today, so "this period excludes today" is true for them. */
  today: string;
  /**
   * Controls the HOST supplies into the report header.
   *
   * ⚠️⚠️ THE CAMPAIGN FILTER COMES THROUGH HERE, AND THAT IS WHY THE SLOT
   * EXISTS. The brief is explicit that the Meta and Google pages keep the
   * EXISTING `MultiSelectFilter` behaviour driven by `appliedCampaignIds` —
   * Scott asked for the same filter, not a new one. Reimplementing it inside
   * the restyle would be a second control with a second idea of what an empty
   * selection means, which is precisely the reversal round 2 made once already.
   * Passing it in also keeps this component free of a shadcn import, so the
   * report's own chrome stays its own.
   */
  controls?: ReactNode;
  /**
   * The location multi-select (§9). ⚠️ OPTIONAL — the staff preview has no
   * `/me` to list a scope from and keeps the plain heading.
   */
  locationPicker?: ReactNode;
  /**
   * A new report is in flight while this one is on screen (§8, 2026-09-04).
   *
   * ⚠️⚠️ IT EXPLAINS A REAL MISMATCH, WHICH IS WHY IT IS NOT COSMETIC. While
   * stale the header renders `report.range` — the PAYLOAD's window — so the dates
   * and the figures agree with each other and nothing on the page lies. But the
   * CONTROL beside it already shows the new selection, so a reader sees
   * "Past 14 days" chosen above "1 Aug — 31 Aug" stated. This cue is what
   * reconciles those two truths: without it the page looks broken, with it it
   * reads as "that is the old answer, the new one is coming".
   *
   * ⚠️ SO IT MUST BE VISIBLE AT THE SAME TIME AS BOTH, never an overlay that
   * hides either half of the mismatch it exists to explain.
   *
   * ⚠️ OPTIONAL, defaulting to false, ONLY for the staff preview — which renders
   * this component from its own query and has no such state. The CLIENT path's
   * own prop is REQUIRED (see `Restyled` in ReportPages).
   */
  refreshing?: boolean;
  /**
   * ⚠️⚠️ AN OPTIONAL CONTROLLED MODE — the `ReportChartCard` grain pattern, and
   * for the same reason: two hosts, two right answers.
   *
   *   SUPPLIED (the client portal) → the report renders NO mode toggle and NO
   *     brand lockup, because `PortalShell` above it carries both. One control
   *     and one lockup per page; two of either is how they end up disagreeing.
   *   OMITTED (the staff "view as client" preview) → the report owns its own
   *     mode exactly as it did, so that page is untouched.
   *
   * ⚠️ The mode ATTRIBUTE is written on this component's root either way. It
   * carries `.kyck-portal`, and a nested `.kyck-portal` re-declares the DEFAULT
   * (dark) role tokens for its own subtree — so a hosted report without the
   * attribute would force itself dark inside a light shell.
   */
  mode?: PortalReportMode;
  onToggleMode?: () => void;
  /**
   * The goal-split Meta page's campaign-goal filter for the audience and
   * creatives. ⚠️ OPTIONAL — the staff preview omits it and shows the panels
   * with the sections unfiltered. Rendered only when the payload carries a
   * goal-split report.
   */
  goalFilter?: {
    value: MetaGoalBucket | null;
    onChange: (next: MetaGoalBucket | undefined) => void;
  };
  /**
   * Website Analytics' studio picker. ⚠️ It only REQUESTS a studio; the server
   * decides whether this scope may see it (a foreign studio 404s).
   */
  onStudioChange?: (studio: string | null) => void;
}

/**
 * Read the stored mode ONCE, lazily.
 *
 * ⚠️ A LAZY INITIALISER, not an effect. An effect would paint the default first
 * and then swap, which flashes a dark report at a client who chose light — and
 * an effect that WROTE would defeat the point of storing a choice at all.
 */
function useReportMode(): [PortalReportMode, () => void] {
  const [mode, setMode] = useState<PortalReportMode>(() =>
    readReportMode(typeof window === 'undefined' ? null : window.localStorage),
  );
  const toggle = (): void => {
    setMode((current) => {
      const next = otherMode(current);
      writeReportMode(typeof window === 'undefined' ? null : window.localStorage, next);
      return next;
    });
  };
  return [mode, toggle];
}

export function RestyledReport(props: RestyledReportProps) {
  const [ownMode, toggleOwnMode] = useReportMode();
  // ⚠️ `hosted` keys off the MODE PROP, not off a separate boolean: a host that
  // supplies one without the other cannot exist, because there would be nothing
  // for the report's own toggle to change.
  const hosted = props.mode !== undefined;
  const mode = props.mode ?? ownMode;
  const toggleMode = props.onToggleMode ?? toggleOwnMode;
  const report = props.overview ?? props.meta ?? props.google ?? props.website;
  // ⚠️⚠️ THE COMPARISON THE HEADER NAMES, off the SAME payload the line above
  // resolved — the identical `??` order, because exactly one of the three is
  // ever non-null (each page fetches only its own). Reading it here rather than
  // widening the header's structural `report` type keeps the two resolutions
  // visibly adjacent, which is what makes "same payload" checkable.
  //
  // ⚠️ A block whose `comparison` is null falls through to the other two nulls
  // and yields null, which is the right answer: no comparison was requested, so
  // the header says nothing about one.
  const comparison =
    props.overview?.combined.comparison ??
    props.meta?.block.comparison ??
    props.google?.block.comparison ??
    null;
  const labels = props.config.labels;

  if (!report) return null;

  const modeAttr = { [REPORT_MODE_ATTR]: mode } as Record<string, string>;

  return (
    <div
      className="kyck-portal rpt-root"
      // ⚠️ `.kyck-portal` is here for the TOKEN SCOPE, not for a brand theme —
      // every `--rpt-*` variable is defined under it. No inline brand style is
      // applied, so a client accent cannot reach this subtree at all.
      {...modeAttr}
    >
      <ReportHeader
        report={report}
        // ⚠️ From whichever payload loaded — all three carry it.
        showsGoogle={report.showsGoogle}
        showsWebsite={report.showsWebsite}
        showsClubwise={'showsClubwise' in report && report.showsClubwise === true}
        config={props.config}
        today={props.today}
        tab={props.tab}
        onTabChange={props.onTabChange}
        mode={mode}
        onToggleMode={toggleMode}
        hosted={hosted}
        controls={props.controls}
        locationPicker={props.locationPicker}
        refreshing={props.refreshing ?? false}
        comparison={comparison}
      />

      {/* ⚠️⚠️ TEMPORARY — see TRACKING_NOTICE for the removal condition. On all
          three tabs, and ONLY for the goal-split brand: `goalSplitReport` is
          resolved server-side from the brand relationship, so no other brand's
          report can show it. */}
      {report.goalSplitReport && (
        <Band>
          <p className="rpt-note" role="note">
            {TRACKING_NOTICE}
          </p>
        </Band>
      )}

      {/* ⚠️⚠️ THE AUTOMOTIVE TEMPLATE replaces the Overview with its Home page. It
          is decided SERVER-SIDE (`automotiveReport` is present only when the
          scope resolved to the automotive template) — never by the browser. */}
      {props.tab === 'overview' && props.overview?.automotiveReport && (
        <AutomotiveHome
          auto={props.overview.automotiveReport}
          google={props.overview.automotiveGoogleReport ?? null}
          labels={labels}
          range={props.overview.range}
          today={props.today}
          showsWebsite={props.overview.showsWebsite}
        />
      )}
      {props.tab === 'overview' && props.overview && !props.overview.automotiveReport && (
        <OverviewPage
          report={props.overview}
          // ⚠️ The goal-split override applies to the Overview and Google pages
          // only — see GOAL_SPLIT_LABEL_OVERRIDE for why never the Meta page.
          labels={goalSplitLabels(labels, props.overview.goalSplitReport)}
          today={props.today}
          mode={mode}
        />
      )}
      {props.tab === 'meta' && props.meta?.automotiveReport && (
        <AutomotiveMetaPage
          auto={props.meta.automotiveReport}
          range={props.meta.range}
          labels={labels}
          today={props.today}
          meta={props.meta}
        />
      )}
      {props.tab === 'meta' && props.meta && !props.meta.automotiveReport && (
        <MetaPage
          report={props.meta}
          labels={labels}
          today={props.today}
          goalFilter={props.goalFilter}
        />
      )}
      {props.tab === 'google' && props.google?.automotiveGoogleReport && (
        <AutomotiveGooglePage
          google={props.google.automotiveGoogleReport}
          range={props.google.range}
          labels={labels}
          today={props.today}
          report={props.google}
        />
      )}
      {props.tab === 'google' && props.google && !props.google.automotiveGoogleReport && (
        <GooglePage
          report={props.google}
          labels={goalSplitLabels(labels, props.google.goalSplitReport)}
          today={props.today}
        />
      )}

      {props.tab === 'clubwise' && props.overview?.crm?.clubwise && (
        <ClubwiseDataPage block={props.overview.crm.clubwise} />
      )}

      {props.tab === 'website' && props.website && (
        <WebsitePage
          report={props.website}
          today={props.today}
          onStudioChange={props.onStudioChange}
        />
      )}

      <ReportFooter />
    </div>
  );
}

/**
 * ⚠️ The kyck wordmark, at the FOOT and nowhere else. The header lockup belongs
 * to the CLIENT'S brand; putting ours there too would be two identities
 * competing for the same corner. The footer is where the report says who made
 * it.
 */
function ReportFooter() {
  return (
    <Band variant="foot">
      <div className="rpt-foot">
        <span className="rpt-display rpt-foot-mark">Kyck</span>
        <span className="rpt-muted">
          © {new Date().getFullYear()} Kyck. Performance lead-gen, Australia &amp; New Zealand.
        </span>
      </div>
    </Band>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function ReportHeader({
  report,
  showsGoogle,
  showsWebsite,
  showsClubwise,
  config,
  today,
  tab,
  onTabChange,
  mode,
  onToggleMode,
  hosted,
  controls,
  locationPicker,
  comparison,
  refreshing,
}: {
  /**
   * ⚠️ The window every delta on the page is against, or null when none was
   * requested. Read from the PAYLOAD — never recomputed in the browser.
   */
  comparison: PortalComparison | null;
  report: { accounts: Array<{ id: string; name: string }>; range: { from: string; to: string } } & {
    accountManager?: PortalOverviewReport['accountManager'];
    brandName?: string | null;
  };
  /** ⚠️ On all three payloads, precisely so the header can read it whichever one loaded. */
  showsGoogle: boolean;
  /** ⚠️ On every payload too — the Website Analytics tab exists only when true. */
  showsWebsite: boolean;
  showsClubwise: boolean;
  config: PortalReportConfig;
  today: string;
  tab: ReportTab;
  onTabChange: (t: ReportTab) => void;
  mode: PortalReportMode;
  onToggleMode: () => void;
  /** ⚠️ True when a shell above supplies the lockup and the mode control. */
  hosted: boolean;
  controls?: ReactNode;
  /**
   * The location multi-select, replacing the club heading (§9).
   *
   * ⚠️ OPTIONAL: the staff preview has no `/me` to list a scope from, so it
   * supplies nothing and keeps the heading it has always had.
   */
  locationPicker?: ReactNode;
  /** ⚠️ REQUIRED here — the outer component defaults it once, for the preview. */
  refreshing: boolean;
}) {
  const theme = config.theme;
  const clubName =
    report.accounts.length === 1
      ? (report.accounts[0]?.name ?? '')
      : `${report.accounts.length} locations`;
  const manager = report.accountManager ?? null;

  return (
    <div className="rpt-top">
      <div className="rpt-shell">
        <div className="rpt-top-row">
          <div>
            {/* ⚠️ THE LOCKUP DEGRADES IN THREE STEPS — logo, brand NAME as text,
                then "Kyck". The middle one is the NORMAL case: only two brands
                have artwork. A logo that FAILS to load falls back to the same
                text rather than a broken image. */}
            {/* ⚠️ OMITTED WHEN HOSTED. The portal shell's header carries the
                lockup one band above this one, and two lockups stacked reads as
                a rendering fault rather than as branding. The STAFF preview
                still renders it — there is no shell above it there. */}
            {/* ⚠️ THE ONE LOCKUP (2026-09-03), shared with the portal shell's
                header. This page had its own `BrandLockup` until then, and two
                implementations of "show the brand" is how the portal came to
                have two looks in the first place.
                ⚠️ NO brand ink. The text is kyck's cream in every case: a brand
                supplies the LOGO and nothing else on this report, and the
                earlier `--portal-ink` read was invisible anyway (it defaults to
                slate-900, on an ink-deep band). */}
            {!hosted && (
              <PortalLockup
                // ⚠️ `?? null` because a multi-brand scope resolves to null BY
                // DESIGN and an older payload may simply omit the field — both
                // mean "no brand", and both must render kyck ALONE.
                brandName={report.brandName ?? null}
                brandLogoUrl={theme?.logoUrl}
                brandLogoAlt={theme?.logoAlt}
              />
            )}
            {/* ⚠️ KYCK'S GREEN. The rule under the lockup used to take the
                client accent; no client colour appears on this report now. */}
            <div className="rpt-accent-rule" />
            {/*
              ⚠️⚠️ THE PICKER REPLACES THE STATIC HEADING (§9, 2026-09-03), and
              it is an OPTIONAL CONTROLLED slot — the `ReportChartCard` grain
              pattern. Supplied → the client's multi-select; omitted → the
              plain heading, which is what the STAFF PREVIEW still renders,
              because there is no `/me` behind it to list a scope from.
            */}
            {locationPicker ?? <p className="rpt-club rpt-display">{clubName}</p>}
            {manager && (
              <p className="rpt-am rpt-muted">
                Account manager <a href={`mailto:${manager.email}`}>{manager.name}</a>
                {manager.phone && (
                  <>
                    {' · '}
                    <a href={`tel:${manager.phone.replace(/\s+/g, '')}`}>{manager.phone}</a>
                  </>
                )}
              </p>
            )}
          </div>
          <div className="rpt-top-controls">
            <div className="rpt-controls">
              {controls}
              {/* ⚠️ THE ONE MODE CONTROL, and when hosted it is not here. The
                  portal shell owns it for the whole portal — otherwise a client
                  who switched on the report page would find the shell still
                  dark, and the choice apparently forgotten on the way home. */}
              {!hosted && (
                <button type="button" aria-pressed={mode === 'light'} onClick={onToggleMode}>
                  {modeToggleLabel(mode)}
                </button>
              )}
            </div>
            {/*
              ⚠️⚠️ THE PENDING CUE, BETWEEN THE CONTROL AND THE STATED PERIOD (§8).
              Deliberately here rather than anywhere else on the page: the control
              above shows the NEW selection, the note below states the window the
              figures actually came from, and this sits between the two saying why
              they differ. It is also where the click happened, so the feedback is
              where the action was.

              ⚠️⚠️ AND IT IS A DISCRETE ELEMENT, NEVER AN OPACITY CHANGE ON THE DATA.
              The obvious implementation — fade the report while loading — has already
              been MEASURED as failing in this project: round 6 §4's muted bars at 55%
              alpha came back 1.84—2.48 against the card, and text figures are held to
              4.5:1, a HIGHER bar than marks. Dimming a client's numbers to convey a
              transient state trades a legibility floor for a hint.

              ⚠️ `aria-live="polite"` and not `role="status"`-with-assertive: a
              reader who has just changed a control does not need to be interrupted,
              only told when it settles.
            */}
            {refreshing && (
              <p className="rpt-refreshing rpt-muted" aria-live="polite">
                <span className="rpt-refreshing-dot" aria-hidden="true" />
                Updating…
              </p>
            )}
            {/* ⚠️ Says the period EXCLUDES today and why — a reader comparing
                the page against their ad account at 4pm would otherwise think
                we were under-reporting. */}
            {/* ⚠️ AND NAMES THE COMPARISON WINDOW: every delta below is against
                it, and the DEFAULT is year-on-year, which is not what a reader
                assumes a percentage means unless the page says so. */}
            <p className="rpt-asof rpt-muted">
              {periodNote(report.range, today, formatReportDate, comparison)}
            </p>
          </div>
        </div>
        <nav className="rpt-tabs" aria-label="Report sections">
          {/* ⚠️⚠️ BUILT FROM A FILTERED ARRAY, not with a conditional inside the
              `.map()`. A conditional there returns a falsy child React silently
              drops, which works and reads as though a tab is being rendered
              blank; filtering says what is actually happening, and it is the
              shape that cannot leave an empty gap in the tab strip. */}
          {TAB_LABELS.filter(
            ([value]) =>
              (value !== 'google' || showsGoogle) &&
              (value !== 'website' || showsWebsite) &&
              (value !== 'clubwise' || showsClubwise),
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              className={tab === value ? 'rpt-tab rpt-tab-on' : 'rpt-tab'}
              // ⚠️ KYCK'S GREEN, not the client accent — and set by a CLASS
              // rather than an inline style, so the selected and unselected
              // states are mutually exclusive selectors and specificity never
              // arbitrates between them (the switch-chip lesson).
              onClick={() => onTabChange(value)}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------

function OverviewPage({
  report,
  labels,
  today,
  mode,
}: {
  report: PortalOverviewReport;
  labels: PortalReportLabels;
  today: string;
  /**
   * ⚠️ PASSED IN, not read again here. §8's ramp is a SELECTED dark palette
   * rather than an automatic flip of the light one, so the charts need to know
   * the mode — and the report already owns exactly one answer to that question,
   * whether it came from the shell or from its own toggle. A second reader would
   * be a second opinion.
   */
  mode: 'light' | 'dark';
}) {
  const combined = report.combined;
  const totals = combined.totals;
  const currency = totals[0]?.currency ?? 'AUD';
  const hero = heroStatsFor(combined, labels);

  // ⚠️⚠️ THE HERO CHANGES WHEN THE CRM IS CONNECTED, and this is the design
  // spine of the whole page. It stops counting the leads the ADS produced and
  // starts counting every enquiry the CLUB recorded — 262 against Meta's 33 on
  // the same page — so a reader will subtract unless the nesting is made
  // visible. §0 of the brief; everything below serves it.
  //
  // ⚠️⚠️ ONE BAND SINCE 2026-09-02. The CRM hero and the "Leads and sales at the
  // club" panel beneath it showed the same five figures twice, so they merged
  // into `CrmHeroBand` — which is a whole band rather than a `ReportHero`, and
  // carries its own caveat, its comparison basis and the nesting bar.

  return (
    <>
      {report.crm ? (
        <CrmHeroBand
          crm={report.crm}
          combined={combined}
          labels={labels}
          range={report.range}
        />
      ) : (
      <ReportHero
        eyebrow={`${labels.leads} · ${formatReportDate(report.range.from)} – ${formatReportDate(report.range.to)}`}
        headline={hero.headline}
        intro={overviewHeroIntro(report)}
        stats={
          report.showsGoogle
            ? [
                ...hero.stats,
                {
                  // ⚠️ FROM `labels`, NOT A LITERAL (§6, 2026-09-03). The whole
                  // point of the rename is one number carrying one name across
                  // the report; a hardcoded "Meta leads" here would put the old
                  // word back on the surface a reader looks at first.
                  label: `Meta ${labels.leads.toLowerCase()}`,
                  value: report.meta.totals.length ? numberFull(sumLeads(report.meta.totals)) : DASH,
                  metric: 'leads',
                  // ⚠️ Each channel's OWN comparison, from its own block — not
                  // the combined one narrowed. `assembleBlock` builds a
                  // `comparison` per channel precisely because their coverage
                  // differs, and Google's is the case that proves it.
                  ...channelLeadsDelta(report.meta, 'Meta'),
                },
                {
                  label: `Google ${labels.leads.toLowerCase()}`,
                  value: report.google.totals.length
                    ? numberFull(sumLeads(report.google.totals))
                    : DASH,
                  metric: 'leads',
                  // ⚠️⚠️ THE DASH ABOVE IS CORRECT AND IS NOT TO BE "FIXED".
                  // `report.google.totals` is empty while the backfill lands
                  // (its history starts 2026-06-30); turning that into a 0
                  // would tell a client their Google advertising produced
                  // nothing. No-data is not zero.
                  ...channelLeadsDelta(report.google, 'Google'),
                },
              ]
            : // ⚠️⚠️ THE "META LEADS" STAT GOES TOO, not just the Google one. With
              // Google suppressed `combined` IS Meta, so the same number under
              // two labels reads as a fault rather than as a breakdown — and a
              // reader who notices they are identical will wonder which of them
              // is wrong.
              hero.stats
        }
      />
      )}

      {/* ⚠️⚠️ DIRECTLY AFTER THE HERO, and before any advertising chart. Putting
          the club's own figures below the campaign tables would bury the outcome
          under the mechanism.

          ⚠️ The WHOLE THING is conditional. `crm` is null when no club in scope
          is connected — these sections are then ABSENT rather than empty,
          because there is no data source to explain and a placeholder would
          promise a feature.

          ⚠️ The figures and the nesting bar are IN the hero band above; what is
          left here is what the hero does not carry.

          ⚠️ SURFACE ALTERNATION (brand rule 5): absorbing the petrol panel into
          the always-dark hero removed a band, so the two that remain still
          alternate — plain (charts) then contrast (sources) then plain
          (advertising). No two light surfaces touch. */}
      {report.crm && (
        <>
          <Band>
            <CrmCharts
              crm={report.crm}
              combined={combined}
              labels={labels}
              periodLabel="this period"
            />
          </Band>
          <Band variant="contrast">
            <LeadSources sources={report.crm.sources} />
          </Band>
        </>
      )}

      <Band>
        {/* ⚠️ THE OLD INTRO EXPLAINED THE GAPS ("months with nothing measured
            are left as gaps rather than drawn as zero") and is GONE. The RULE is
            unchanged and still lives in series.ts; what carries it to the reader
            is the TOOLTIP, which says "not measured" in words on an unmeasured
            bucket. That was verified before the sentence was cut — a broken line
            with nothing to explain it was Scott's own earlier bug report.

            ⚠️ The heading is SENTENCE CASE. Scott wrote "Advertising Results
            Over Period"; brand rule 11 forbids Title Case headings, so it ships
            lower — flagged to him rather than changed silently.

            ⚠️ AND THE PERIOD MOVED OUT OF IT (2026-09-01). Three headings sat in
            two shapes — this one against "Meta advertising results" and "Google
            advertising results" — and the two channel pages set the pattern. The
            dates were the only thing making this one different, so they went to
            the intro, where the reader looking for them is already looking. */}
        {/* ⚠️⚠️ WITH A CRM CONNECTED THIS SECTION IS A PART, NOT A RIVAL. A
            reader arriving here after a 262 must already know they are looking
            at a subset — "Advertising results" beside "Enquiries" reads as two
            competing totals, and the difference between them is the subtraction
            §0 exists to prevent. Sentence case (rule 11). */}
        <SectionHeading
          title={report.crm ? 'What the advertising produced' : 'Advertising results'}
          intro={`${formatReportDate(report.range.from)} – ${formatReportDate(
            report.range.to,
          )}. Choose between past 12 months or reporting period below.`}
        />
        <ReportChartCard
          title="Month by month"
          source={{ kind: 'comparison', block: combined }}
          metrics={['leads', 'spend', 'costPerLead']}
          labels={labels}
          currency={currency}
          periodLabel="this period"
        />
      </Band>

      {/* ⚠️⚠️ THE WHOLE BAND, not just its contents. An empty contrast band is a
          visible stripe of colour with nothing in it — a gap where a section
          used to be, which is worse than the section being absent. */}
      {report.showsGoogle && (
      <Band variant="contrast">
        <SectionHeading
          title="Meta against Google"
          // ⚠️ The ratio's NAME comes from `labels`, so it matches the chip on
          // the chart beneath it (it is "Cost per conversion" for the
          // goal-split brand, and unchanged for everyone else).
          intro={`Spend and leads stack because the parts genuinely sum to the whole. ${labels.costPerLead} is a ratio, so it is drawn as two lines instead.`}
        />
        <ReportChartCard
          title="Meta against Google"
          source={{ kind: 'channel', meta: report.meta, google: report.google }}
          metrics={['spend', 'leads', 'costPerLead', 'share']}
          labels={labels}
          currency={currency}
          periodLabel="this period"
        />
        <div className="rpt-card-grid rpt-card-grid--2" style={{ marginTop: 14 }}>
          <ChannelCard
            title="Meta"
            channelRole="a"
            block={report.meta}
            otherBlock={report.google}
            labels={labels}
          />
          <ChannelCard
            title="Google"
            channelRole="b"
            block={report.google}
            otherBlock={report.meta}
            labels={labels}
          />
        </div>
      </Band>
      )}

      {/*
        ALL LOCATIONS (§4 phase A, 2026-09-03).

        ⚠️ `hasLeaderboard` rather than a length check: a single-location scope
        has no by-location story, and the server sends null rather than one row
        repeating the hero.

        ⚠️⚠️ AN ORDINARY BAND, DELIBERATELY — not a hero or a contrast one. §4d:
        if the leaderboard reads as a SECOND results section the fix is
        HIERARCHY, not a longer caveat. The combined figure stays the hero;
        this sits below it on the page's plainest surface.
      */}
      {hasLeaderboard(report.byLocation) && report.byLocation && (
        <Band>
          <SectionHeading
            title="Your locations"
            intro="Every location on this login, for the selected period. Sort any column."
          />
          {/* ⚠️ THE AVERAGES ARE COMPUTED FROM THE ROWS ON SCREEN, so the tile's
              denominator and the table's rows cannot disagree about how many
              clubs have figures. */}
          <GroupAverageTiles
            averages={groupAverages(report.byLocation)}
            labels={labels}
            // ⚠️ The sales tile appears only where the CRM does. A labelled
            // empty tile reads as a broken figure; no tile reads as a section
            // that does not apply.
            showSales={!!report.crm}
          />
          <LocationsTable
            rows={report.byLocation}
            labels={labels}
            tab="overview"
            uncountedCrmClubs={report.crm?.uncountedClubs.map((c) => c.name) ?? []}
          />
        </Band>
      )}

      {/* ⚠️⚠️ §8 — DIRECTLY AFTER THE LEADERBOARD, and that placement is the
          argument. The table answers "how is each location doing"; these answer
          "how is each location CHANGING", which is the same question one level
          up and reads as a non-sequitur anywhere else on the page. It also puts
          the growth charts immediately below the absolute figures they index, so
          a reader who wants the level has just seen it.

          ⚠️ ABSENT for a single-location scope — the server returns null, so
          there is no group to compare and nothing to hide here. */}
      {report.groupSeries && (
        <GroupChartsBand series={report.groupSeries} labels={labels} mode={mode} />
      )}

      {report.campaigns.length > 0 && (
        <Band variant="contrast">
          {/* ⚠️⚠️ "BOTH CHANNELS TOGETHER" IS A TWO-CHANNEL CLAIM AND HAS TO GO
              WITH GOOGLE. Caught in the browser, and it is the interesting miss:
              the source-shape test asserts on the WORD "Google", and this
              sentence never says it — it just tells a Meta-only client their
              table covers two channels. The TITLE is unchanged, because "where
              the money went" is true of one channel. */}
          <SectionHeading
            title="Where the money went"
            intro={
              report.showsGoogle
                ? 'Every campaign that spent in this period, both channels together. Sort any column.'
                : 'Every campaign that spent in this period. Sort any column.'
            }
          />
          <CombinedCampaignsTable
            rows={report.campaigns}
            labels={labels}
            showsGoogle={report.showsGoogle}
          />
        </Band>
      )}

      {/* ⚠️ Provenance sits on a CONTRAST band, not the footer one — it is
          content the client is meant to read, not chrome. The footer below it
          carries the kyck mark. */}
      <Band variant="contrast">
        <ProvenanceBlock lines={report.provenance} />
      </Band>
    </>
  );
}

/**
 * The Overview hero's one sentence.
 *
 * ⚠️⚠️ FOR THE GOAL-SPLIT BRAND THE TOTAL COUNTS META PURCHASES (2026-09-25),
 * so it is Meta leads + Meta purchases + Google conversions — the same figure as
 * the Meta page's headline plus Google. The round-2 sentence ("…Meta purchases
 * are reported on the Meta page") described the old total and is gone.
 * ⚠️ Website leads are not named separately: they are inside Meta leads already.
 *
 * ⚠️ Every other brand keeps the original wording, unchanged.
 */
function overviewHeroIntro(report: PortalOverviewReport): string {
  if (report.goalSplitReport) {
    return report.showsGoogle
      ? 'Meta leads, Meta purchases and Google conversions for this period, each counted once.'
      : 'Meta leads and Meta purchases for this period, each counted once.';
  }
  return report.showsGoogle
    ? 'Google calls these conversions and Meta calls them leads; combined, this is every person who put their hand up.'
    : 'Everyone who put their hand up through your Meta ads in this period.';
}

function MetaPage({
  report,
  labels,
  today,
  goalFilter,
}: {
  report: PortalMetaReport;
  labels: PortalReportLabels;
  today: string;
  goalFilter?: RestyledReportProps['goalFilter'];
}) {
  const currency = report.block.totals[0]?.currency ?? 'AUD';
  const hero = heroStatsFor(report.block, labels);
  const goal = report.goalReport;
  const filterControl =
    goal && goalFilter ? (
      <MetaGoalFilterButtons value={report.appliedGoalBucket} onChange={goalFilter.onChange} />
    ) : null;
  return (
    <>
      {/* ⚠️⚠️ THE GOAL-SPLIT PAGE replaces the one combined Meta figure with
          CONVERSIONS at the top and four campaign-goal panels beneath it —
          Scott: one combined number "makes the data look bad and not a true
          reflection of the activity". Resolved server-side from the brand. */}
      {goal ? (
        <>
          {/* ⚠️ META ONLY (round 2) — no `showsGoogle`: the Meta page never
              consults Google, whatever the scope runs. */}
          <MetaGoalHeadline goal={goal} range={report.range} />
          <MetaGoalPanels goal={goal} labels={labels} />
        </>
      ) : (
        /* ⚠️ "Leads" on the Meta page and "Conversions" on Google — each channel
            keeps its own word, and the combined page says both. Resolved through
            PortalReportLabels, which exists for exactly this. */
        <ReportHero
          eyebrow={`Meta ${labels.leads.toLowerCase()} · ${formatReportDate(report.range.from)} – ${formatReportDate(report.range.to)}`}
          headline={hero.headline}
          intro="What Meta returned in this period, and what each lead cost."
          stats={hero.stats}
        />
      )}

      <Band>
        {/* ⚠️ SENTENCE CASE. Scott wrote "Meta Advertising Results";
            kyck-brand rule 11 forbids Title Case headings, and this is the
            second time it has come up. */}
        <SectionHeading
          title="Meta advertising results"
          intro="Choose between past 12 months or reporting period."
        />
        <ReportChartCard
          title="Meta advertising results"
          source={{ kind: 'comparison', block: report.block }}
          metrics={['leads', 'spend', 'costPerLead']}
          labels={labels}
          currency={currency}
          periodLabel="this period"
        />
      </Band>

      {report.campaigns.length > 0 && (
        <Band variant="contrast">
          <SectionHeading title="Campaign detail" />
          <ChannelCampaignsTable rows={report.campaigns} labels={labels} />
        </Band>
      )}

      {/*
        ALL LOCATIONS, this channel's own figures (§4d, 2026-09-03).

        ⚠️⚠️ NO CRM COLUMNS HERE, and that is not an omission. A sale cannot be
        attributed to a channel, so a sales column sitting in a row of this
        channel's spend and leads would be read as this channel's sales. The
        server passes no CRM map to a channel leaderboard, so the columns cannot
        appear by oversight — this component simply renders what it is given.
      */}
      {hasLeaderboard(report.byLocation) && report.byLocation && (
        <Band>
          <SectionHeading
            title="Your locations"
            intro="Every location on this login, for the selected period. Sort any column."
          />
          <GroupAverageTiles
            averages={groupAverages(report.byLocation)}
            labels={labels}
            showSales={false}
          />
          <LocationsTable rows={report.byLocation} labels={labels} tab="meta" />
        </Band>
      )}

      {/* ⚠️⚠️ OMITTED ENTIRELY ON A GROUP REPORT (round 8 §3, 2026-09-04) — a
          COMPOSITION decision, made where the scope is already known, and
          deliberately NOT a `return null` inside the explainer.

          On a group the section rendered a heading and one paragraph saying why
          it had nothing to show: a section whose entire content is an apology.
          The absence is STRUCTURAL AND PERMANENT there — Meta reports per pull
          window, so two locations can be answered by two different periods and
          summing them mixes grains inside one percentage.

          ⚠️⚠️ A `return null` IN THAT COMPONENT HAS ALREADY HIDDEN A REAL FAULT
          ONCE. On the Pyrmont report `demographics` was NOT null — the read
          succeeded and returned `grain: 'none'` with a reason — and a guard
          threw the reason away along with the heading. That silence turned a
          failed nightly job into FOUR ROUNDS OF INVESTIGATION on 2026-08-31.
          A SINGLE LOCATION'S ABSENCE IS A FAULT SOMEONE SHOULD CHASE and must
          always say so, which is why the gate lives here and the panel no
          longer knows the account count at all.

          ⚠️ `isGroupScope` — the same rule as the creative cards' club label, the
          group charts and the location picker. `report.accounts` is
          POST-NARROWING, so a drill-down to one club KEEPS its audience
          section, which is exactly right. */}
      {/* ⚠️ THE GOAL FILTER (round 2) sits in the heading of BOTH the audience
          and the creative sections, over ONE shared state, so the two cannot be
          filtered differently. It resolves each campaign's goal by campaign id —
          the same resolution the panels use. Absent on the staff preview and on
          every non-goal-split report. */}
      {!isGroupScope(report.accounts) && (
        <Band>
          <AudiencePanel
            demographics={report.demographics}
            today={today}
            goalBucket={goal ? report.appliedGoalBucket : null}
            filter={filterControl}
          />
        </Band>
      )}

      <Band variant="contrast">
        {/* ⚠️ `hadSpend` decides WHICH absence sentence is true when the grid is
            empty — "nothing ran" is a fact about the month, "ads ran but Meta
            returned no creative detail" is a gap at our end. */}
        <CreativeGrid
          rows={report.creatives}
          // ⚠️ POST-NARROWING, which is what makes the gate right: a drill-down
          // to one club has one account here, so the labels drop off and the
          // report's own heading names it instead.
          accounts={report.accounts}
          labels={labels}
          hadSpend={report.campaigns.some((c) => c.spend > 0)}
          // ⚠️ Only the goal-split page switches the grid's measures; everywhere
          // else `goal` is null and the grid is exactly as before.
          goal={goal ? { bucket: report.appliedGoalBucket } : null}
          filter={filterControl}
        />
      </Band>

      {/* ⚠️ AFTER THE CREATIVE GRID, in its own band. What ran, then how it was
          delivered — and the framing sentence inside says outright that this is
          not a measure of performance. */}
      <Band>
        <DeliveryDiagnostics
          campaigns={report.campaigns}
          block={report.block}
          labels={labels}
          periodLabel="this period"
        />
      </Band>
    </>
  );
}

function GooglePage({
  report,
  labels,
}: {
  report: PortalGoogleReport;
  labels: PortalReportLabels;
  today: string;
}) {
  const currency = report.block.totals[0]?.currency ?? 'AUD';
  const hero = heroStatsFor(report.block, labels);
  return (
    <>
      <ReportHero
        eyebrow={`Google ${labels.leads.toLowerCase()} · ${formatReportDate(report.range.from)} – ${formatReportDate(report.range.to)}`}
        headline={hero.headline}
        intro="What Google returned in this period, and what each conversion cost."
        stats={hero.stats}
      />

      <Band>
        {/* ⚠️ SENTENCE CASE. Scott wrote "Google Advertising Results";
            kyck-brand rule 11 forbids Title Case headings, and this is the
            second time it has come up. */}
        <SectionHeading
          title="Google advertising results"
          intro="Choose between past 12 months or reporting period."
        />
        <ReportChartCard
          title="Google advertising results"
          source={{ kind: 'comparison', block: report.block }}
          metrics={['leads', 'spend', 'costPerLead']}
          labels={labels}
          currency={currency}
          periodLabel="this period"
        />
      </Band>

      {/* ⚠️ PROMINENT, and BEFORE the tables — these are the numbers that
          translate into a decision. Omitted entirely when nothing is
          measurable. */}
      <Band variant="contrast">
        <CompetitivePanel competitive={report.competitive} />
      </Band>

      {report.campaigns.length > 0 && (
        <Band>
          <BudgetGrainSection report={report} labels={labels} />
        </Band>
      )}

      {/*
        ALL LOCATIONS, this channel's own figures (§4d, 2026-09-03).

        ⚠️⚠️ NO CRM COLUMNS HERE, and that is not an omission. A sale cannot be
        attributed to a channel, so a sales column sitting in a row of this
        channel's spend and leads would be read as this channel's sales. The
        server passes no CRM map to a channel leaderboard, so the columns cannot
        appear by oversight — this component simply renders what it is given.

        ⚠️ PLACED IN THE EQUIVALENT POSITION to Meta's: after this channel's own
        detail tables and before the diagnostics, so the three pages read the
        same way down.
      */}
      {hasLeaderboard(report.byLocation) && report.byLocation && (
        <Band>
          <SectionHeading
            title="Your locations"
            intro="Every location on this login, for the selected period. Sort any column."
          />
          <GroupAverageTiles
            averages={groupAverages(report.byLocation)}
            labels={labels}
            showSales={false}
          />
          <LocationsTable rows={report.byLocation} labels={labels} tab="google" />
        </Band>
      )}

      {report.searchTerms.length > 0 && (
        <Band variant="contrast">
          <SectionHeading
            title="What people searched for"
            intro="The actual words typed before a click. Sort any column."
          />
          <SearchTermsTable
            rows={report.searchTerms}
            labels={labels}
            capped={report.searchTermsCapped}
            total={report.searchTermsTotal}
          />
        </Band>
      )}

      {/* ⚠️⚠️ LAST ON THIS PAGE, AND DELIBERATELY FAR FROM THE COMPETITIVE
          PANEL. That panel's headline is impression SHARE — a percentage of an
          auction — and this band's is impressionS, a count. Set side by side
          they read as two views of one measure, and a client who conflates them
          concludes their 38% share means 38 of the impressions below. On Meta
          there is no competitive panel, so the band sits higher; the placement
          differing between the two pages is the deliberate part. */}
      <Band>
        <DeliveryDiagnostics
          campaigns={report.campaigns}
          block={report.block}
          labels={labels}
          periodLabel="this period"
        />
      </Band>
    </>
  );
}

type PortalReportLabels = PortalReportConfig['labels'];

// Referenced by the hero's per-currency helper; re-exported so the page module
// stays the one import site for the restyle.
export { perCurrency, moneyFull, moneyRound };

/**
 * "Where the budget goes", at CAMPAIGN or AD GROUP grain.
 *
 * ⚠️⚠️ THE LABEL IS "AD GROUP", NOT "AD SET". Scott said "Adset", which is
 * Meta's word — this is the Google page, and the client's own Google Ads account
 * calls them ad groups. Matching the platform the client can go and check is
 * worth more than matching the Meta page's vocabulary. A deliberate deviation
 * from his wording, and his to overrule.
 *
 * ⚠️⚠️ THE TOGGLE IS ABSENT, NOT PRESENT-AND-EMPTY, when there are no ad-group
 * rows at all. A PMAX-only client clicking a control and getting an empty table
 * reads that as a fault, and there is nothing the ad-group view could tell them.
 *
 * ⚠️ DEFAULTS TO CAMPAIGN — it is the COMPLETE view (Performance Max has no ad
 * groups, so the ad-group view genuinely holds less money) and it is what this
 * page showed before the toggle existed.
 *
 * ⚠️ `SwitchGroup`, not a second switch. Its selected and unselected states are
 * mutually exclusive CLASSES, so specificity never arbitrates between them — the
 * bug that shipped once already on this report's chips.
 */
function BudgetGrainSection({
  report,
  labels,
}: {
  report: PortalGoogleReport;
  labels: PortalReportLabels;
}) {
  const [grain, setGrain] = useState<'campaign' | 'adGroup'>('campaign');
  const hasAdGroups = report.adGroups.rows.length > 0;
  const showingAdGroups = hasAdGroups && grain === 'adGroup';
  const excluded = report.adGroups.excluded;

  return (
    <>
      <SectionHeading
        title="Where the budget goes"
        aside={
          hasAdGroups ? (
            <SwitchGroup
              ariaLabel="Budget detail level"
              value={grain}
              onChange={setGrain}
              options={[
                { value: 'campaign', label: 'Campaign' },
                { value: 'adGroup', label: 'Ad group' },
              ]}
            />
          ) : undefined
        }
      />
      {/* ⚠️⚠️ THE EXCLUSION LEADS THE TABLE, and only on the ad-group view. It
          names the COST, so the drop is accounted for rather than discovered —
          and it points back at the campaign view, which is what makes the toggle
          safe rather than a trap. A caveat read after a total has been believed
          is not a caveat. */}
      {showingAdGroups && excluded && (
        <p className="rpt-note rpt-muted">{adGroupExclusionNote(excluded, moneyRound)}</p>
      )}
      {showingAdGroups ? (
        <AdGroupsTable rows={report.adGroups.rows} labels={labels} />
      ) : (
        <ChannelCampaignsTable rows={report.campaigns} labels={labels} />
      )}
    </>
  );
}
